import React, { useState } from 'react';
import { Tooltip } from 'antd';
import type { NutrientKey } from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import {
  NUTRIENT_PIE_COLORS,
  NUTRIENT_SHORT_LABELS,
} from 'pages/FarmsPage/Workspace/Insights/computeLiveTotals';
import { scaleForDisplay } from 'pages/FarmsPage/Workspace/Insights/insightUtils';

/* ── Layout constants ────────────────────────────────────────────────────────
   All values are in SVG user units (1:1 with px at viewBox scale). Height is
   dynamic (depends on item count) — width is fixed and scales via the
   surrounding "w-full" CSS.
   ─────────────────────────────────────────────────────────────────────────── */

const VIEW_W = 220;
const LABEL_W = 46;
const PAD_R = 6;
const BAR_W = VIEW_W - LABEL_W - PAD_R;
const ROW_H = 20;
const ROW_GAP = 10;
const ROW_PITCH = ROW_H + ROW_GAP;
const PAD_T = ROW_GAP / 2;
const PAD_B = 6;

/**
 * % of a bar's own width representing 100% of that nutrient's own Plan
 * value — the remaining width is headroom for an over-plan bar to visibly
 * extend into (capped at the row's full width, recoloured to a warning
 * colour, rather than scaled further — see buildRow).
 */
const PLAN_WIDTH_PCT = 65;

/* ── Format helpers ──────────────────────────────────────────────────────────*/

function formatQty(value: number): string {
  const { value: v, unit } = scaleForDisplay(value, 'kg');
  return `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit}`;
}

function getTooltipTitle(item: NutrientProgressItem): string {
  const label = NUTRIENT_SHORT_LABELS[item.key];
  if (item.plan <= 0)
    return `${label}: ${formatQty(item.actual)} applied — not planned`;
  if (item.actual <= 0)
    return `${label}: 0kg / ${formatQty(item.plan)} applied`;
  return `${label}: ${formatQty(item.actual)} / ${formatQty(item.plan)} applied`;
}

/* ── Component ───────────────────────────────────────────────────────────── */

export interface NutrientProgressItem {
  key: NutrientKey;
  /** Plan value (kg/ha). 0 means no plan recorded for this nutrient. */
  plan: number;
  /** Actual value (kg/ha). 0 means nothing applied yet. */
  actual: number;
}

interface NutritionProgressChartProps {
  items: NutrientProgressItem[];
  /** Controlled hover — lets a table row above the chart drive the same highlight as hovering a bar directly. Falls back to internal state when omitted. */
  hoveredKey?: string | null;
  onHoverKeyChange?: (key: string | null) => void;
}

/**
 * Horizontal bar chart showing, per nutrient, Actual applied so far against
 * its own Plan target. Replaced an earlier radial ("wheel") version — with
 * more than a handful of nutrients active, wedges bunched together and thin
 * reference ticks were nearly impossible to distinguish; a vertical list of
 * bars scales cleanly regardless of how many nutrients are in scope, and
 * each bar sits directly next to its own label with no identity ambiguity.
 *
 * - Each nutrient gets its own row (dot + short symbol, coloured with its
 *   own colour from NUTRIENT_PIE_COLORS).
 * - A pale track (that nutrient's own colour at low opacity) fills the row
 *   up to PLAN_WIDTH_PCT of the bar's width, marking 100% of that nutrient's
 *   own Plan — only drawn when a Plan value exists for it. Each nutrient is
 *   scaled independently against its own Plan, not a shared scale across
 *   nutrients — macro (N/P/K) and micro (B/Cu/Fe...) nutrients differ by
 *   orders of magnitude, so a shared linear scale would make micronutrient
 *   bars imperceptible.
 * - A solid bar shows Actual, scaled the same way. When Actual exceeds Plan
 *   (or Plan is 0 — an unplanned application), the bar fills the row's full
 *   width and switches to a warning colour instead of overflowing — there's
 *   nowhere left to grow, so the colour itself is the signifier.
 * - Neither element is drawn when its own value is 0 — a plan-only nutrient
 *   shows just its pale track, a nutrient with no plan and no actual
 *   doesn't appear at all (filtered by the caller).
 * - Hovering a row (or the linked table row) shows the exact Plan vs Actual
 *   figures in an AntD Tooltip near the cursor, e.g. "N: 6.8t / 8.19t applied"
 *   — visible regardless of scroll position.
 */
const NutritionProgressChart: React.FC<NutritionProgressChartProps> = ({
  items,
  hoveredKey,
  onHoverKeyChange,
}) => {
  const [internalHoverKey, setInternalHoverKey] = useState<string | null>(
    null
  );
  const hoverKey = hoveredKey !== undefined ? hoveredKey : internalHoverKey;
  const setHoverKey = onHoverKeyChange ?? setInternalHoverKey;

  if (items.length === 0) return null;

  const rows = items.map((item, i) => {
    const ratio =
      item.plan > 0 ? item.actual / item.plan : item.actual > 0 ? Infinity : 0;
    const isOver = ratio > 1;
    const actualWidthPct =
      item.actual <= 0 ? 0 : isOver ? 100 : ratio * PLAN_WIDTH_PCT;
    const yTop = PAD_T + i * ROW_PITCH;
    return { item, ratio, isOver, actualWidthPct, yTop };
  });

  const viewHeight = PAD_T + items.length * ROW_PITCH - ROW_GAP + PAD_B;

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${viewHeight}`}
      className="w-full"
      role="img"
      aria-label="Nutrient plan vs actual progress chart"
    >
      {rows.map((r) => {
        const color = NUTRIENT_PIE_COLORS[r.item.key];
        const dimmed = !!hoverKey && hoverKey !== r.item.key;
        const rowCenterY = r.yTop + ROW_H / 2;
        return (
          <g key={r.item.key}>
            {/* Plan track */}
            {r.item.plan > 0 && (
              <rect
                x={LABEL_W}
                y={r.yTop}
                width={(PLAN_WIDTH_PCT / 100) * BAR_W}
                height={ROW_H}
                rx={2}
                fill={color}
                opacity={dimmed ? 0.08 : 0.22}
                className="transition-opacity duration-150"
              />
            )}
            {/* Actual bar */}
            {r.item.actual > 0 && (
              <rect
                x={LABEL_W}
                y={r.yTop}
                width={(r.actualWidthPct / 100) * BAR_W}
                height={ROW_H}
                rx={2}
                fill={r.isOver ? 'var(--color-danger)' : color}
                opacity={dimmed ? 0.25 : 1}
                className="transition-opacity duration-150"
              />
            )}

            {/* Label */}
            <circle
              cx={5}
              cy={rowCenterY}
              r={4}
              fill={color}
              opacity={dimmed ? 0.4 : 1}
            />
            <text
              x={13}
              y={rowCenterY}
              textAnchor="start"
              dominantBaseline="middle"
              fontSize={10}
              fontWeight="600"
              fill="var(--color-charcoal)"
              opacity={dimmed ? 0.4 : 1}
            >
              {NUTRIENT_SHORT_LABELS[r.item.key]}
            </text>

            {/* Full-row invisible hit area — covers label + bar so hovering either highlights this nutrient. */}
            <Tooltip
              title={getTooltipTitle(r.item)}
              placement="top"
              mouseLeaveDelay={0}
            >
              <rect
                x={0}
                y={Math.max(0, r.yTop - ROW_GAP / 2)}
                width={VIEW_W}
                height={ROW_PITCH}
                fill="transparent"
                onMouseEnter={() => setHoverKey(r.item.key)}
                onMouseLeave={() => setHoverKey(null)}
                className="cursor-pointer"
              />
            </Tooltip>
          </g>
        );
      })}
    </svg>
  );
};

export default NutritionProgressChart;
